import { useEffect, useState } from 'react';
import { LocalizedText, lt, showSuccessAlert } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { FormErrors } from '../../../../types';
import { useRateCreate } from '../../../../queries';
import { RateContainer } from './RateContainer';
import { PreparedValues } from './types';
import { RATE_KEY_FIELD_NAME } from './fields';
import { RateFormProvider } from './hooks/RateFormProvider';

interface CreateRateContainerProps {
    enterpriseIds: string[];
    isOpened: boolean;
    onConfirm: () => Promise<void>;
    onCancel: () => void;
    isDependentRate: boolean;
}

export const CreateRateContainer = ({
    enterpriseIds,
    isOpened,
    onConfirm,
    onCancel,
    isDependentRate,
}: CreateRateContainerProps) => {
    const [formErrors, setFormErrors] = useState<FormErrors>();
    const { mutate, isPending } = useRateCreate();

    useEffect(() => {
        if (!isOpened) {
            setFormErrors(undefined);
        }
    }, [isOpened]);

    const onCreateSuccess = async () => {
        showSuccessAlert({
            title: lt(isDependentRate ? 'DependentRatesCreated' : 'BaseRatesCreated'),
        });
        await onConfirm();
    };

    const handleSubmit = (values: PreparedValues) =>
        mutate(
            {
                ...values,
                isEnabled: Boolean(values.isEnabled),
                enterpriseIds,
            },
            {
                onSuccess: onCreateSuccess,
                onError: (e) => {
                    if (e.message.includes('rate key')) {
                        setFormErrors({
                            field: RATE_KEY_FIELD_NAME,
                            message: e.message,
                        });
                    }
                },
            }
        );

    const baseRateTitle =
        enterpriseIds?.length > 1 ? (
            <LocalizedText textKey={TextKey.CreateBaseRateInBulk} />
        ) : (
            <LocalizedText textKey={TextKey.CreateBaseRate} />
        );
    const dependentRateTitle =
        enterpriseIds?.length > 1 ? (
            <LocalizedText textKey={TextKey.CreateDependentRateInBulk} />
        ) : (
            <LocalizedText textKey={TextKey.CreateDependentRate} />
        );

    const title = isDependentRate ? dependentRateTitle : baseRateTitle;

    return (
        <RateFormProvider enterpriseIds={enterpriseIds}>
            <RateContainer
                formErrors={formErrors}
                active={isOpened}
                dependent={isDependentRate}
                enterpriseIds={enterpriseIds}
                onCancel={onCancel}
                onSubmit={handleSubmit}
                title={title}
                submitButtonTextKey={TextKey.Create}
                isLoading={isPending}
            />
        </RateFormProvider>
    );
};
